import { Deps } from './fp';
import {
  escapeMarkdownHtml as escapeMarkdownHtmlValue,
  escapeMarkdownLinkLabel,
  escapeMarkdownText as escapeMarkdownTextValue,
} from './markdown';

export const SAFE_EDITOR_LINK_URL_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);
export const FALLBACK_SAFE_EDITOR_LINK_URL = '#';
const SAFE_EDITOR_LINK_URL_PLACEHOLDERS = new Set(['http://', 'https://', 'mailto:']);

export type EditorMarkdownDeps = {
  safeUrlProtocols?: ReadonlySet<string>;
  blockSeparator?: string;
  trailingNewline?: string;
  tableLineBreakReplacement?: string;
  minCodeFenceLength?: number;
  maxCodeBlockLanguageLength?: number;
  getImageNodeData?(node: unknown): unknown;
  renderImageNode?(node: unknown, imageData: unknown): string | undefined;
  readRootChildren?(editorState: unknown): unknown[];
  readChildren?(node: Record<string, unknown>): unknown[];
  readString?(value: Record<string, unknown>, key: string): string | undefined;
  escapeText?(value: string): string;
  escapeLinkLabel?(value: string): string;
  escapeHtml?(value: string): string;
  sanitizeUrl?(url: string | undefined): string | undefined;
};

export type ParseSerializedEditorStateDeps = {
  parseEditorState?(value: string): unknown;
};

export function parseSerializedEditorState(
  value: string | null | undefined,
  { parseEditorState = parseJsonEditorState }: ParseSerializedEditorStateDeps = {},
) {
  if (!value) {
    return null;
  }

  return parseEditorState(value);
}

export const serializeEditorStateToMarkdown = Deps.pipe(
  getEditorMarkdownRootChildren,
  renderEditorMarkdownBlockNodes,
  finalizeEditorMarkdown,
);

export function getEditorMarkdownRootChildren(
  editorState: unknown,
  { readRootChildren = defaultReadRootChildren }: Pick<EditorMarkdownDeps, 'readRootChildren'> = {},
) {
  return readRootChildren(editorState);
}

export function renderEditorMarkdownBlockNodes(nodes: unknown[], deps: EditorMarkdownDeps = {}) {
  const blockSeparator = deps.blockSeparator ?? '\n\n';
  return nodes
    .map((node) => renderBlockNode(node, deps))
    .filter(Boolean)
    .join(blockSeparator);
}

export function finalizeEditorMarkdown(
  markdown: string,
  { trailingNewline = '\n' }: Pick<EditorMarkdownDeps, 'trailingNewline'> = {},
) {
  const trimmedMarkdown = markdown.trimEnd();
  return trimmedMarkdown ? `${trimmedMarkdown}${trailingNewline}` : '';
}

function renderBlockNode(node: unknown, deps: EditorMarkdownDeps): string {
  if (!isRecord(node)) {
    return '';
  }

  switch (node['type']) {
    case 'heading': {
      const tag = typeof node['tag'] === 'string' ? node['tag'] : 'h1';
      const depth = Number(tag.slice(1));
      const level = Number.isInteger(depth) ? Math.max(1, Math.min(6, depth)) : 1;
      return `${'#'.repeat(level)} ${renderInlineChildren(node, deps)}`.trimEnd();
    }

    case 'paragraph':
      return renderInlineChildren(node, deps);

    case 'quote':
      return renderBlockquote(renderInlineChildren(node, deps) || renderChildBlocks(node, deps));

    case 'list':
      return renderList({ node, depth: 0 }, deps);

    case 'code':
      return renderCodeBlock(node, deps);

    case 'horizontalrule':
    case 'horizontal-rule':
      return '---';

    case 'table':
      return renderTable(node, deps);

    case 'image':
      return renderEditorMarkdownImageNode(node, deps) ?? '';

    default:
      return renderInlineChildren(node, deps) || renderChildBlocks(node, deps);
  }
}

function renderInlineChildren(node: Record<string, unknown>, deps: EditorMarkdownDeps) {
  return getChildren(node, deps)
    .map((child) => renderInlineNode(child, deps))
    .join('')
    .trimEnd();
}

function renderInlineNode(node: unknown, deps: EditorMarkdownDeps): string {
  if (!isRecord(node)) {
    return '';
  }

  if (node['type'] === 'text') {
    return formatTextNode(node, deps);
  }

  if (node['type'] === 'linebreak') {
    return '\n';
  }

  if (node['type'] === 'link' || node['type'] === 'autolink') {
    const label = renderInlineChildren(node, deps) || readString(node, 'url', deps) || '';
    const sanitizeUrl = deps.sanitizeUrl ?? ((url) => sanitizeMarkdownUrl(url, deps));
    const url = sanitizeUrl(readString(node, 'url', deps));
    return url ? `[${escapeLinkLabel(label, deps)}](${url})` : escapeMarkdownText(label, deps);
  }

  if (isImageNode(node)) {
    return renderEditorMarkdownImageNode(node, deps) ?? '';
  }

  return renderInlineChildren(node, deps) || renderBlockNode(node, deps);
}

function formatTextNode(node: Record<string, unknown>, deps: EditorMarkdownDeps) {
  const rawText = readString(node, 'text', deps) ?? '';
  const format = typeof node['format'] === 'number' ? node['format'] : 0;
  const isBold = (format & 1) !== 0;
  const isItalic = (format & 2) !== 0;
  const isStrikethrough = (format & 4) !== 0;
  const isCode = (format & 16) !== 0;

  if (isCode) {
    return renderCodeSpan(rawText, deps);
  }

  let formattedText = escapeMarkdownText(rawText, deps);

  if (isBold && isItalic) {
    formattedText = `***${formattedText}***`;
  } else if (isBold) {
    formattedText = `**${formattedText}**`;
  } else if (isItalic) {
    formattedText = `*${formattedText}*`;
  }

  if (isStrikethrough) {
    formattedText = `~~${formattedText}~~`;
  }

  return formattedText;
}

function renderList(
  { node, depth }: { node: Record<string, unknown>; depth: number },
  deps: EditorMarkdownDeps,
): string {
  const listType = readString(node, 'listType', deps) ?? readString(node, 'tag', deps);
  let orderedIndex = typeof node['start'] === 'number' ? node['start'] : 1;

  return getChildren(node, deps)
    .map((child) => {
      if (!isRecord(child)) {
        return '';
      }

      const marker = listType === 'number' || listType === 'ol' ? `${orderedIndex++}.` : '-';
      const indent = '  '.repeat(depth);
      const nested = getChildren(child, deps).filter(isListNode);
      const content = getChildren(child, deps)
        .filter((grandchild) => !(isRecord(grandchild) && grandchild['type'] === 'list'))
        .map((grandchild) => renderInlineNode(grandchild, deps))
        .join('')
        .trim();
      const nestedContent = nested.map((listNode) =>
        renderList({ node: listNode, depth: depth + 1 }, deps),
      );

      return [`${indent}${marker} ${content}`, ...nestedContent].filter(Boolean).join('\n');
    })
    .filter(Boolean)
    .join('\n');
}

function renderCodeBlock(node: Record<string, unknown>, deps: EditorMarkdownDeps) {
  const language = readString(node, 'language', deps) ?? '';
  const code = getChildren(node, deps)
    .map((child) => {
      if (!isRecord(child)) {
        return '';
      }

      if (child['type'] === 'linebreak') {
        return '\n';
      }

      return readString(child, 'text', deps) ?? '';
    })
    .join('');
  const delimiter = buildCodeFenceDelimiter(code, deps);
  return `${delimiter}${sanitizeCodeBlockLanguage(language, deps)}\n${code}\n${delimiter}`;
}

function renderTable(node: Record<string, unknown>, deps: EditorMarkdownDeps) {
  const rows = getChildren(node, deps)
    .filter(isRecord)
    .map((row) =>
      getChildren(row, deps)
        .filter(isRecord)
        .map((cell) => renderTableCell(cell, deps)),
    );

  if (rows.length === 0) {
    return '';
  }

  const columnCount = Math.max(...rows.map((row) => row.length));
  const normalizedRows = rows.map((row) =>
    Array.from({ length: columnCount }, (_value, index) => row[index] ?? ''),
  );
  const [headerRow, ...bodyRows] = normalizedRows;
  if (!headerRow) {
    return '';
  }

  return [
    renderMarkdownTableRow(headerRow),
    renderMarkdownTableRow(headerRow.map(() => '---')),
    ...bodyRows.map(renderMarkdownTableRow),
  ].join('\n');
}

function renderTableCell(cell: Record<string, unknown>, deps: EditorMarkdownDeps) {
  const content = renderInlineChildren(cell, deps) || renderChildBlocks(cell, deps);
  return content
    .replace(/\s*\n\s*/g, deps.tableLineBreakReplacement ?? ' / ')
    .replace(/\|/g, '\\|');
}

function renderMarkdownTableRow(cells: string[]) {
  return `| ${cells.join(' | ')} |`;
}

function renderChildBlocks(node: Record<string, unknown>, deps: EditorMarkdownDeps) {
  return renderEditorMarkdownBlockNodes(getChildren(node, deps), deps);
}

function renderBlockquote(content: string) {
  return content
    .split('\n')
    .map((line) => `> ${line}`.trimEnd())
    .join('\n');
}

function getChildren(node: Record<string, unknown>, deps: EditorMarkdownDeps): unknown[] {
  const readChildren = deps.readChildren ?? defaultReadChildren;
  return readChildren(node);
}

function readString(value: Record<string, unknown>, key: string, deps: EditorMarkdownDeps) {
  const reader = deps.readString ?? defaultReadString;
  return reader(value, key);
}

function isListNode(node: unknown): node is Record<string, unknown> {
  return isRecord(node) && node['type'] === 'list';
}

function isImageNode(node: Record<string, unknown>) {
  return node['type'] === 'image';
}

export function sanitizeEditorLinkUrl(
  url: string | undefined,
  deps: Pick<EditorMarkdownDeps, 'safeUrlProtocols'> = {},
) {
  if (!url) {
    return undefined;
  }

  const safeUrlProtocols = deps.safeUrlProtocols ?? SAFE_EDITOR_LINK_URL_PROTOCOLS;
  const trimmedUrl = url.trim();
  if (!trimmedUrl || hasUnsafeUrlCharacters(trimmedUrl) || trimmedUrl.startsWith('//')) {
    return undefined;
  }

  if (SAFE_EDITOR_LINK_URL_PLACEHOLDERS.has(trimmedUrl.toLowerCase())) {
    return trimmedUrl;
  }

  try {
    const parsed = new URL(trimmedUrl);
    if (!safeUrlProtocols.has(parsed.protocol)) {
      return undefined;
    }
    return trimmedUrl;
  } catch {
    if (
      trimmedUrl.startsWith('#') ||
      (trimmedUrl.startsWith('/') && !trimmedUrl.startsWith('//'))
    ) {
      return trimmedUrl;
    }
    return undefined;
  }
}

export function isSafeEditorLinkUrl(
  url: string | undefined,
  deps: Pick<EditorMarkdownDeps, 'safeUrlProtocols'> = {},
) {
  return sanitizeEditorLinkUrl(url, deps) !== undefined;
}

export function sanitizeSerializedEditorLinks<T extends string | null | undefined>(
  serializedEditorState: T,
): T {
  if (typeof serializedEditorState !== 'string') {
    return serializedEditorState;
  }

  try {
    const parsedEditorState: unknown = JSON.parse(serializedEditorState);
    const sanitizedEditorState = sanitizeSerializedEditorNode(parsedEditorState);
    return (
      sanitizedEditorState.changed
        ? JSON.stringify(sanitizedEditorState.value)
        : serializedEditorState
    ) as T;
  } catch {
    return serializedEditorState;
  }
}

function sanitizeSerializedEditorNode(value: unknown): { value: unknown; changed: boolean } {
  if (Array.isArray(value)) {
    let changed = false;
    const children = value.map((child) => {
      const sanitizedChild = sanitizeSerializedEditorNode(child);
      changed ||= sanitizedChild.changed;
      return sanitizedChild.value;
    });
    return { value: changed ? children : value, changed };
  }

  if (!isRecord(value)) {
    return { value, changed: false };
  }

  let changed = false;
  const sanitizedNode: Record<string, unknown> = {};

  for (const [key, childValue] of Object.entries(value)) {
    const sanitizedChild = sanitizeSerializedEditorNode(childValue);
    sanitizedNode[key] = sanitizedChild.value;
    changed ||= sanitizedChild.changed;
  }

  if (isLinkSerializedNode(value)) {
    const url = value['url'];
    if (typeof url === 'string' && !isSafeEditorLinkUrl(url)) {
      sanitizedNode['url'] = FALLBACK_SAFE_EDITOR_LINK_URL;
      changed = true;
    }
  }

  return { value: changed ? sanitizedNode : value, changed };
}

function isLinkSerializedNode(value: Record<string, unknown>) {
  return value['type'] === 'link' || value['type'] === 'autolink';
}

function sanitizeMarkdownUrl(url: string | undefined, deps: EditorMarkdownDeps) {
  const sanitizedUrl = sanitizeEditorLinkUrl(url, deps);
  return sanitizedUrl ? encodeURI(sanitizedUrl).replace(/[()]/g, encodeURIComponent) : undefined;
}

function hasUnsafeUrlCharacters(url: string) {
  return Array.from(url).some((char) => {
    const code = char.charCodeAt(0);
    return code <= 31 || code === 127 || /\s/.test(char);
  });
}

function escapeMarkdownText(value: string, deps: EditorMarkdownDeps) {
  const escapeText = deps.escapeText;
  if (escapeText) {
    return escapeText(value);
  }

  return escapeMarkdownTextValue(value, { escapeHtml: deps.escapeHtml });
}

function escapeLinkLabel(value: string, deps: EditorMarkdownDeps) {
  const escapeLabel = deps.escapeLinkLabel;
  if (escapeLabel) {
    return escapeLabel(value);
  }

  return escapeMarkdownLinkLabel(value, { escapeHtml: deps.escapeHtml });
}

function escapeMarkdownHtml(value: string, deps: EditorMarkdownDeps) {
  return escapeMarkdownHtmlValue(value, { escapeHtml: deps.escapeHtml });
}

function renderCodeSpan(value: string, deps: EditorMarkdownDeps) {
  const delimiter = buildBacktickDelimiter(value);
  const escapedValue = escapeMarkdownHtml(value, deps);
  const padding = shouldPadCodeSpan(escapedValue) ? ' ' : '';
  return `${delimiter}${padding}${escapedValue}${padding}${delimiter}`;
}

function buildBacktickDelimiter(value: string) {
  return '`'.repeat(getLongestBacktickRun(value) + 1);
}

function buildCodeFenceDelimiter(value: string, deps: EditorMarkdownDeps) {
  const minCodeFenceLength = deps.minCodeFenceLength ?? 3;
  return '`'.repeat(Math.max(minCodeFenceLength, getLongestBacktickRun(value) + 1));
}

function getLongestBacktickRun(value: string) {
  return Math.max(0, ...Array.from(value.matchAll(/`+/g), (match) => match[0].length));
}

function sanitizeCodeBlockLanguage(value: string, deps: EditorMarkdownDeps) {
  return value.replace(/[^A-Za-z0-9_+.#-]/g, '').slice(0, deps.maxCodeBlockLanguageLength ?? 80);
}

function shouldPadCodeSpan(value: string) {
  return (
    value.startsWith('`') || value.endsWith('`') || value.startsWith(' ') || value.endsWith(' ')
  );
}

function renderEditorMarkdownImageNode(node: unknown, deps: EditorMarkdownDeps) {
  const imageData = deps.getImageNodeData?.(node);
  if (!imageData) {
    return undefined;
  }

  return deps.renderImageNode?.(node, imageData) ?? '';
}

function parseJsonEditorState(value: string): unknown {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
}

function defaultReadRootChildren(editorState: unknown) {
  const root = isRecord(editorState) ? editorState['root'] : undefined;
  return isRecord(root) ? defaultReadChildren(root) : [];
}

function defaultReadChildren(node: Record<string, unknown>): unknown[] {
  const children = node['children'];
  return Array.isArray(children) ? children : [];
}

function defaultReadString(value: Record<string, unknown>, key: string): string | undefined {
  const target = value[key];
  return typeof target === 'string' ? target : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
